import { ReactElement, useState, useCallback, memo } from 'react';
import { Collapse, Divider, Stack } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

import { pluralize } from '~utils/commons';
import { getImportRowErrorTypeLabel } from '~utils/order';

import { ImportErrorsDetailGroupProps } from './types';
import {
  GroupContainer,
  GroupHeader,
  GroupHeaderInfo,
  GroupSeverityDot,
  GroupReference,
  GroupMeta,
  GroupRows,
  GroupRow,
  GroupRowHeader,
  GroupRowMessage,
} from './styles';

const ImportErrorsDetailGroup = ({
  group,
  hideClientCode,
}: ImportErrorsDetailGroupProps): ReactElement => {
  const [expanded, setExpanded] = useState(false);

  const handleToggle = useCallback(() => setExpanded((prev) => !prev), []);

  const label = getImportRowErrorTypeLabel(group.errorTypeId, group.errorType);

  return (
    <GroupContainer>
      <GroupHeader
        expanded={expanded}
        hasErrors={group.hasErrors}
        onClick={handleToggle}
      >
        <GroupHeaderInfo>
          <GroupSeverityDot hasErrors={group.hasErrors} />

          <GroupReference variant="body2">{label}</GroupReference>

          <GroupMeta variant="caption">
            {pluralize({
              singular: 'fila',
              count: group.rows.length,
              includeCount: true,
            })}
          </GroupMeta>
        </GroupHeaderInfo>

        {expanded ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </GroupHeader>

      <Collapse in={expanded} unmountOnExit>
        <GroupRows divider={<Divider />}>
          {group.rows.map((row, index) => {
            const parts = [`Fila ${row.rowNumber}`];

            if (row.referenceId) parts.push(row.referenceId);
            if (!hideClientCode && row.clientCode) parts.push(row.clientCode);
            if (row.article) parts.push(row.article);
            if (row.externalOrderId) parts.push(row.externalOrderId);

            return (
              <GroupRow key={`import-error-row-${row.rowNumber}-${index}`}>
                <GroupRowHeader variant="caption">
                  {parts.join(' · ')}
                </GroupRowHeader>

                <Stack spacing={0.5}>
                  {row.messages.map((message, messageIndex) => (
                    <Stack
                      key={`import-error-row-${row.rowNumber}-message-${messageIndex}`}
                      direction="row"
                      spacing={0.5}
                    >
                      <GroupRowMessage
                        variant="body2"
                        fontWeight="bold"
                        color="text.secondary"
                      >
                        -
                      </GroupRowMessage>

                      <GroupRowMessage variant="body2">
                        {message}
                      </GroupRowMessage>
                    </Stack>
                  ))}
                </Stack>
              </GroupRow>
            );
          })}
        </GroupRows>
      </Collapse>
    </GroupContainer>
  );
};

export default memo(ImportErrorsDetailGroup);
